import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { fmtDate, imgUrl } from "../lib/shop";
import { AdminTitle, Toggle } from "./AdminLayout";
import { UploadButton } from "./Images";
import { Categories } from "./Manage";

const Field = ({ label, value, onChange, area, testid }) => (
  <div>
    <label className="label">{label}</label>
    {area ? <textarea rows={3} className="field py-3" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      : <input className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
  </div>
);

const Card = ({ title, children }) => <section className="card-dark p-5 space-y-4"><h2 className="font-display text-3xl">{title}</h2>{children}</section>;

function PaymentStatus() {
  const { data } = useFetch("/admin/payment-config");
  if (!data) return null;
  const row = (l, ok) => <p className="flex justify-between text-sm"><span>{l}</span><span className={ok ? "text-emerald-400" : "text-red-400"}>{ok ? "Set" : "Missing"}</span></p>;
  return (
    <Card title="iKhokha payments">
      <p className="text-sm" data-testid="payment-provider-status">Active provider: <b className={data.provider === "ikhokha" ? "text-emerald-400" : "text-amber-300"}>{data.provider === "demo" ? "DEMO (mocked)" : data.provider}</b></p>
      {row("IKHOKHA_API_BASE", data.api_base_set)}{row("IKHOKHA_APP_ID", data.app_id_set)}{row("IKHOKHA_APP_SECRET", data.secret_set)}{row("IKHOKHA_ENTITY_ID (optional)", data.entity_id_set)}
      <p className="text-xs text-zinc-500">Credentials are stored securely on the server only and can never be edited or viewed from this dashboard.</p>
    </Card>
  );
}

export default function Settings() {
  const { data } = useFetch("/admin/settings");
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return <p className="text-zinc-400">Loading...</p>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const sub = (group, k) => (v) => setS({ ...s, [group]: { ...s[group], [k]: v } });
  const save = async () => {
    try { const { id, ...body } = s; await api.put("/admin/settings", body); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <AdminTitle title="Settings"><button onClick={save} className="btn-gold !min-h-[44px]" data-testid="settings-save-button">Save settings</button></AdminTitle>
      <div className="grid xl:grid-cols-2 gap-6">
        <Card title="Store information">
          <Field label="Store name" value={s.store_name} onChange={(v) => set("store_name", v)} testid="settings-store-name" />
          <Field label="Tagline" value={s.tagline} onChange={(v) => set("tagline", v)} testid="settings-tagline" />
          <Field label="Contact email" value={s.contact_email} onChange={(v) => set("contact_email", v)} testid="settings-email" />
          <Field label="Phone / WhatsApp number" value={s.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} testid="settings-whatsapp" />
          <Field label="WhatsApp pre-filled message" value={s.whatsapp_message} onChange={(v) => set("whatsapp_message", v)} testid="settings-whatsapp-message" />
          <Field label="Announcement bar" value={s.announcement} onChange={(v) => set("announcement", v)} testid="settings-announcement" />
          <div className="flex items-center gap-3"><img src={imgUrl(s.logo_url)} alt="" className="w-16 h-16 object-contain bg-black rounded" />
            <UploadButton multiple={false} label="Replace logo" testid="settings-logo-upload" onUploaded={(a) => a[0] && set("logo_url", a[0].url)} /></div>
          <p className="text-xs text-zinc-500">Note: the header & footer use the bundled brand logo; an uploaded logo is used for sharing previews.</p>
        </Card>
        <Card title="Homepage hero">
          <Field label="Eyebrow" value={s.hero?.eyebrow} onChange={sub("hero", "eyebrow")} testid="settings-hero-eyebrow" />
          <Field label="Subtitle" area value={s.hero?.subtitle} onChange={sub("hero", "subtitle")} testid="settings-hero-subtitle" />
          <div className="flex items-center gap-3"><img src={imgUrl(s.hero?.image)} alt="" className="w-24 h-16 object-cover rounded" />
            <UploadButton multiple={false} label="Replace hero image" testid="settings-hero-upload" onUploaded={(a) => a[0] && sub("hero", "image")(a[0].url)} /></div>
        </Card>
        <Card title="Social media links">
          {["instagram", "facebook", "tiktok", "x"].map((k) => <Field key={k} label={k} value={s.socials?.[k]} onChange={sub("socials", k)} testid={`settings-social-${k}`} />)}
        </Card>
        <Card title="Default SEO">
          <Field label="Site title" value={s.seo?.title} onChange={sub("seo", "title")} testid="settings-seo-title" />
          <Field label="Meta description" area value={s.seo?.description} onChange={sub("seo", "description")} testid="settings-seo-description" />
        </Card>
        <Card title="Store policies (shown on Returns, FAQ & Shipping)">
          <p className="text-xs text-amber-300">Replace every “[OWNER TO CONFIRM]” before launch.</p>
          {[["return_period_days", "Return period (e.g. 14 days)"], ["exchange_conditions", "Exchange conditions"], ["return_courier_paid_by", "Who pays return courier"], ["refund_method", "Refund method"], ["refund_timeline", "Refund timeline"], ["delivery_timeframe", "Delivery timeframe"]].map(([k, l]) =>
            <Field key={k} label={l} value={s.policies?.[k]} onChange={sub("policies", k)} testid={`settings-policy-${k.replace(/_/g, "-")}`} />)}
        </Card>
        <Card title="Newsletter">
          <Toggle label="Show newsletter signup" checked={s.newsletter?.enabled} onChange={sub("newsletter", "enabled")} testid="settings-newsletter-enabled" />
          <Field label="Heading" value={s.newsletter?.heading} onChange={sub("newsletter", "heading")} testid="settings-newsletter-heading" />
          <Field label="Text" area value={s.newsletter?.text} onChange={sub("newsletter", "text")} testid="settings-newsletter-text" />
        </Card>
        <PaymentStatus />
      </div>
      <div className="mt-10"><Categories /></div>
    </div>
  );
}

export function InboxPage() {
  const enq = useFetch("/admin/enquiries");
  const subs = useFetch("/admin/newsletter");
  const mark = async (id, status) => { await api.put(`/admin/enquiries/${id}`, { status }); enq.reload(); };
  const csv = () => {
    const blob = new Blob(["email,date\n" + (subs.data || []).map((x) => `${x.email},${x.created_at}`).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "mwezzy-subscribers.csv"; a.click();
  };
  return (
    <div>
      <AdminTitle title="Inbox" />
      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-3" data-testid="admin-enquiries">
          <h2 className="font-display text-3xl">Contact enquiries</h2>
          {(enq.data || []).map((e) => (
            <div key={e.id} className={`card-dark p-4 text-sm ${e.status === "resolved" ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap justify-between gap-2"><b>{e.subject}</b><span className="text-xs text-zinc-400">{fmtDate(e.created_at)}</span></div>
              <p className="text-xs text-zinc-400">{e.name} · <a className="underline" href={`mailto:${e.email}`}>{e.email}</a> {e.phone && `· ${e.phone}`}</p>
              <p className="mt-2 whitespace-pre-line text-zinc-200">{e.message}</p>
              <div className="flex gap-2 mt-3">{["new", "read", "resolved"].map((st) => <button key={st} onClick={() => mark(e.id, st)} className={`chip !min-h-[34px] !text-xs ${e.status === st ? "chip-active" : ""}`}>{st}</button>)}</div>
            </div>
          ))}
          {enq.data?.length === 0 && <p className="text-zinc-500 text-sm">No enquiries yet.</p>}
        </div>
        <div className="card-dark p-5 h-fit" data-testid="admin-subscribers">
          <div className="flex justify-between items-center"><h2 className="font-display text-3xl">Subscribers ({subs.data?.length || 0})</h2><button onClick={csv} className="chip !min-h-[36px] !text-xs" data-testid="subscribers-export">Export CSV</button></div>
          <div className="mt-3 max-h-[60vh] overflow-y-auto">{(subs.data || []).map((x) => <p key={x.email} className="text-sm py-1.5 border-b border-white/5">{x.email}</p>)}</div>
        </div>
      </div>
    </div>
  );
}
